import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useToastActions } from '@/entities/toast';
import WarningSvg from '@/shared/assets/icons/toast/warning.svg';
import CloseSvg from '@/shared/assets/icons/ui-kit/close-modal.svg';
import FullscreenSvg from '@/shared/assets/icons/ui-kit/fullscreen.svg';
import { Box } from '@/shared/ui/box';
import { Button } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import { Text } from '@/shared/ui/text';

import { createModalHook } from '../../lib/create-modal-hook.helper';
import * as Styled from './common-modal.styles';
import { CommonModalProps } from './common-modal.types';

// TODO: need to expand on the topic and not use notAuth
export const CommonModalComponent = ({
	title,
	description,
	okText,
	cancelText = 'Cancel',
	onClose,
	onConfirm,
	children,
	content,
	isLoading,
	disabled,
	allowClose = true,
	fullscreen = false,
	width = '420px',
	sizeTitle = 'm',
	notAuth = false,
}: CommonModalProps) => {
	const { t } = useTranslation('common');
	const { addToast } = useToastActions();
	const { modal } = useTheme();
	const modalRef = useRef<HTMLDivElement>(null);

	const toggleFullscreen = () => {
		if (modalRef.current) {
			if (!document.fullscreenElement) {
				modalRef.current.requestFullscreen().catch(() => {
					addToast({
						type: 'error',
						title: t('toast.error.title'),
						message: 'Something went wrong. Please reload the page',
					});
				});
			} else {
				document.exitFullscreen().catch(() => {
					addToast({
						type: 'error',
						title: t('toast.error.title'),
						message: 'Something went wrong. Please reload the page',
					});
				});
			}
		}
	};

	return (
		<Styled.Wrapper ref={modalRef} direction="column" width={width}>
			<Box align="center" padding="16px" justify="space-between" borderBottom={modal.border}>
				<Text size={sizeTitle} label={title} weight={600} style={{ flex: 1 }} />
				{fullscreen && <Styled.Icon Svg={FullscreenSvg} onClick={toggleFullscreen} />}
				{allowClose && <Styled.Icon Svg={CloseSvg} onClick={onClose} />}
			</Box>
			{Boolean(children) ? (
				<Box padding="16px" flex={1} justify="center">
					{children}
				</Box>
			) : (
				<Styled.Content align="center" justify="center" direction="column" padding="16px" gap={12}>
					<Icon Svg={WarningSvg} />
					<Styled.Description label={description ?? ''} align="center" />
					{content}
				</Styled.Content>
			)}
			<Box justify="end" gap={16} padding="16px" borderTop={modal.border} background={modal.backgroundActions}>
				{allowClose && (
					<Button
						label={cancelText || t('modal.button.cancel')}
						variant={notAuth ? 'login' : 'outlined'}
						onClick={onClose}
						size="xs"
					/>
				)}
				<Button
					label={okText || t('modal.button.ok')}
					onClick={onConfirm ?? onClose}
					size="xs"
					variant={notAuth ? 'login' : 'contained'}
					isLoading={isLoading}
					disabled={disabled}
				/>
			</Box>
		</Styled.Wrapper>
	);
};

export default createModalHook<CommonModalProps>((props) => () => <CommonModalComponent {...props} />);
